import { useState, useEffect } from 'react';
import { Plus, Search, Trash2, ArrowLeft, Save, Beaker, Minus, Settings } from 'lucide-react';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { api } from '../../services/api';
import { useConfirm } from '../../hooks/use-confirm';
import { toast } from 'sonner';

export function DesignMixManager() {
    const [models, setModels] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    // Edit/Create State
    const [isEditing, setIsEditing] = useState(false);
    const [newModel, setNewModel] = useState<any>({ model_name: '', details: [] });
    const [editingId, setEditingId] = useState<number | null>(null);
    const [materials, setMaterials] = useState<any[]>([]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await api.designMix.list();
            setModels(res);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const fetchMaterials = async () => {
        try {
            // Get GRC materials specifically to have access to default conversion ratios
            const res = await api.materials.listGRC();
            setMaterials(res);
        } catch (e) {
            console.error("Error fetching materials", e);
        }
    };

    const { confirm } = useConfirm();

    useEffect(() => {
        fetchData();
        fetchMaterials();
    }, []);

    const handleAddIngredient = () => {
        setNewModel({
            ...newModel,
            details: [...(newModel.details || []), { material_id: '', quantity: 0 }]
        });
    };

    const removeIngredient = (index: number) => {
        const updated = [...newModel.details];
        updated.splice(index, 1);
        setNewModel({ ...newModel, details: updated });
    };

    const updateIngredient = (index: number, field: string, value: any) => {
        const updated = [...newModel.details];
        updated[index][field] = value;

        // If material changed, auto-populate conversion ratio from master
        if (field === 'material_id') {
            const mat = materials.find(m => m.material_id === parseInt(value));
            if (mat) {
                updated[index].quantity = mat.conversion_ratio || 0;
            }
        }

        setNewModel({ ...newModel, details: updated });
    };

    const handlePopulateStandard = async () => {
        const ok = await confirm('This will replace all current ingredients with the standard GRC list from materials master. Proceed?', { confirmText: 'Replace', cancelText: 'Cancel' });
        if (!ok) return;
        const standardDetails = materials
            .filter(m => m.conversion_ratio > 0)
            .map(m => ({
                material_id: m.material_id.toString(),
                quantity: m.conversion_ratio
            }));

        setNewModel({
            ...newModel,
            details: standardDetails
        });
    };

    const handleSave = async () => {
        try {
            const payload = {
                model_name: newModel.model_name,
                details: newModel.details.map((d: any) => ({
                    material_id: parseInt(d.material_id),
                    quantity: parseFloat(d.quantity)
                }))
            };
            if (editingId) {
                await api.designMix.update(editingId, payload);
            } else {
                await api.designMix.create(payload);
            }
            setIsEditing(false);
            setNewModel({ model_name: '', details: [] });
            setEditingId(null);
            fetchData();
        } catch (e) {
            console.error(e);
            toast.error("Failed to save design mix");
        }
    };

    const handleDelete = async (id: number) => {
        const ok = await confirm('Are you sure? This will delete the mix model and all its ingredients.', { variant: 'destructive', confirmText: 'Delete', cancelText: 'Cancel' });
        if (!ok) return;
        try {
            await api.designMix.delete(id);
            toast.success('Design mix deleted');
            fetchData();
        } catch (e) {
            toast.error("Failed to delete");
        }
    };

    const filteredModels = models.filter(m =>
        m.model_name.toLowerCase().includes(search.toLowerCase())
    );

    // --- FORM VIEW ---
    if (isEditing) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => { setIsEditing(false); setNewModel({ model_name: '', details: [] }); setEditingId(null); }}>
                        <ArrowLeft className="w-5 h-5 text-slate-500" />
                    </Button>
                    <h2 className="text-xl font-semibold text-slate-800">{editingId ? 'Edit Design Mix Model' : 'New Design Mix Model'}</h2>
                </div>

                <Card className="max-w-3xl">
                    <CardHeader>
                        <CardTitle>Model Definition</CardTitle>
                        <CardDescription>Define the mix formula and its ingredients.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-8">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Model Name</label>
                            <Input
                                placeholder="e.g., GRC Standard Mix"
                                value={newModel.model_name || ''}
                                onChange={e => setNewModel({ ...newModel, model_name: e.target.value })}
                            />
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-slate-700">Ingredients</h3>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="secondary" onClick={handleAddIngredient}>
                                        <Plus className="w-4 h-4 mr-1" /> Add Ingredient
                                    </Button>
                                </div>
                            </div>

                            {newModel.details?.length === 0 && (
                                <div className="p-8 text-center border-2 border-dashed rounded-lg text-slate-400">
                                    No ingredients added. Click "Add Ingredient" to start.
                                </div>
                            )}

                            {newModel.details?.map((det: any, idx: number) => (
                                <div key={idx} className="flex items-end gap-4 p-4 bg-slate-50 rounded-md border">
                                    <div className="flex-1 space-y-1">
                                        <label className="text-xs font-medium text-slate-500">Material</label>
                                        <select
                                            className="w-full p-2 border rounded-md text-sm bg-white"
                                            value={det.material_id}
                                            onChange={e => updateIngredient(idx, 'material_id', e.target.value)}
                                        >
                                            <option value="">Select Material...</option>
                                            {materials.map(m => (
                                                <option key={m.material_id} value={m.material_id}>
                                                    {m.material_name} ({m.unit})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="w-32 space-y-1">
                                        <label className="text-xs font-medium text-slate-500">Conv Ratio</label>
                                        <Input
                                            type="number"
                                            step="0.001"
                                            value={det.quantity}
                                            onChange={e => updateIngredient(idx, 'quantity', e.target.value)}
                                        />
                                    </div>
                                    <Button variant="ghost" size="icon" className="text-red-500 hover:bg-red-50" onClick={() => removeIngredient(idx)}>
                                        <Minus className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-3 pt-4 border-t">
                            <Button onClick={handleSave} disabled={!newModel.model_name || newModel.details.length === 0}>
                                <Save className="w-4 h-4 mr-2" /> {editingId ? 'Update Mix Model' : 'Save Mix Model'}
                            </Button>
                            <Button variant="outline" onClick={() => { setIsEditing(false); setNewModel({ model_name: '', details: [] }); setEditingId(null); }}>
                                Cancel
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // --- LIST VIEW ---
    return (
        <Card className="h-full flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between py-4 border-b">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Beaker className="w-5 h-5 text-blue-600" /> Design Mix Models
                </CardTitle>
                <div className="flex gap-2">
                    <div className="relative w-64">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Search models..."
                            className="pl-8 h-9"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <Button onClick={() => { setIsEditing(true); setEditingId(null); setNewModel({ model_name: '', details: [] }); }}>
                        <Plus className="w-4 h-4 mr-1" /> New Model
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-auto p-0">
                <div className="grid gap-4 p-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {loading ? (
                        <div className="col-span-full text-center text-slate-500">Loading...</div>
                    ) : filteredModels.length === 0 ? (
                        <div className="col-span-full text-center text-slate-500 py-12">No mix models found</div>
                    ) : (
                        filteredModels.map((model) => (
                            <Card key={model.design_mix_model_id} className="overflow-hidden hover:shadow-md transition-shadow">
                                <CardHeader className="bg-slate-50 py-3 border-b">
                                    <div className="flex justify-between items-center">
                                        <h3 className="font-semibold text-slate-800">{model.model_name}</h3>
                                        <div className="flex gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 text-slate-400 hover:text-blue-600"
                                                onClick={() => {
                                                    setNewModel({
                                                        model_name: model.model_name,
                                                        details: model.details.map((d: any) => ({
                                                            material_id: d.material_id,
                                                            quantity: d.quantity || d.conversion_ratio
                                                        }))
                                                    });
                                                    setEditingId(model.design_mix_model_id);
                                                    setIsEditing(true);
                                                }}
                                            >
                                                <Settings className="w-3 h-3" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 text-slate-400 hover:text-red-600"
                                                onClick={() => handleDelete(model.design_mix_model_id)}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-4 text-sm">
                                    <div className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">Ingredients</div>
                                    <ul className="space-y-1">
                                        {model.details?.map((det: any, i: number) => (
                                            <li key={i} className="flex justify-between">
                                                <span className="text-slate-700">{det.material_name}</span>
                                                <span className="font-mono text-slate-500">{det.quantity || det.conversion_ratio || '-'} {det.unit}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
